import { groupMediaBySubGroup } from './buildMediaListItems'
import { resolveImageSlotRatioForRequired, isExactRatioMatch, isImageMedia } from './media'

// Youtube items have no ratio/dimensions concept and must never be pruned.
// Anything else we can't classify (no ratio, no width/height) is kept too —
// safer to leave unclassifiable data alone than silently drop it.
const isPrunable = (item) => !item?.youtube_video_id && (item?.ratio || (item?.width && item?.height))

// Tie-break priority when multiple images collapse into the same slot and
// none is an exact match for it (e.g. switching to Meta collapses 1:1, 4:5,
// and 1.91:1 into one feed slot — if there's no exact 1:1, prefer 4:5 over
// 1.91:1). Deliberately separate from media.js's MEDIA_ASPECT_RATIOS, which
// exists for a different purpose (nearest-match classification) — the two
// happen to share the same order today, but coupling them would make this
// priority silently change if that list is ever reordered for an unrelated
// reason.
const RATIO_PRIORITY = ['1:1', '4:5', '1.91:1', '9:16']

const rankRatio = (ratio) => {
  const index = RATIO_PRIORITY.indexOf(ratio)
  return index === -1 ? Infinity : index
}

// Reconciles a creative's flat media array to a new required-slot-ratio set
// (e.g. after a platform/campaign-type switch on step 1). Within each
// co-create sub-group, keeps at most one image per slot in the new required
// list; on a collision (multiple images now nearest-match the same slot),
// the one whose own ratio ranks highest in RATIO_PRIORITY wins — an exact
// match for the slot always ranks highest, and among non-exact candidates
// (only possible under Meta's collapsed feed slot) 4:5 beats 1.91:1. Items
// that don't fit any slot in the old set simply never collide and are kept
// as long as they still resolve to *some* slot in the new set — this is
// nearest-match, not strict rejection, matching upload validation elsewhere.
//
// googleExactRatios (default none — behaves exactly as above) additionally
// makes those specific slots strict: an image nearest-matching one of them
// without being pixel-exact never wins the slot at all — even with no other
// candidate to lose to — and is dropped. Videos are exempt (Google-exactness
// is an image-only concept; a video sitting in that slot is there for Meta).
export const pruneMediaForRequiredSlots = (media = [], requiredSlotRatios, googleExactRatios = []) => {
  if (!Array.isArray(media) || !media.length) {
    return media
  }

  const keepIds = new Set()

  groupMediaBySubGroup(media).forEach((group) => {
    const bestBySlot = new Map()

    group.items.forEach((item) => {
      if (!isPrunable(item)) {
        keepIds.add(item.id)
        return
      }

      const slot = resolveImageSlotRatioForRequired(item, requiredSlotRatios)

      if (!slot) {
        keepIds.add(item.id)
        return
      }

      if (googleExactRatios.includes(slot) && isImageMedia(item) && !isExactRatioMatch(item, slot)) {
        return
      }

      const existing = bestBySlot.get(slot)

      if (!existing || rankRatio(item.ratio) < rankRatio(existing.ratio)) {
        bestBySlot.set(slot, item)
      }
    })

    bestBySlot.forEach((item) => keepIds.add(item.id))
  })

  return media.filter((item) => keepIds.has(item.id))
}
